import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Grid,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  ImageList,
  ImageListItem,
  Divider,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Container,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ExploreIcon from '@mui/icons-material/TravelExplore';
import PostAddIcon from '@mui/icons-material/PostAdd';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import PaymentIcon from '@mui/icons-material/Payment';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { request } from '../../Utils/request';
import { setUser } from '../../store/slices/User';
import { logout } from '../../store/slices/AuthSlice';
import { clearFcmToken } from '../../store/slices/FcmSlice';
import { unregisterFcmToken } from '../../services/firebaseMessaging';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { signOutGoogle } from '../../services/googleAuth';
import { PostUI } from '../Post/PostUI';
import InstaVideoIcon from '@mui/icons-material/VideoCameraFront';
import { followUser, fetchUserPosts } from '../../services/CommonFunctions';
import { getOrCreateChat } from '../../services/CommonApiCall';
import { clearUserCache } from '../../services/users';
import { resetPosts } from '../../store/slices/PostSlice';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

// Reusable stat item component
interface StatItemProps {
  value: number | string;
  label: string;
  xs?: number;
  onClick?: () => void;
}

const StatItem: React.FC<StatItemProps> = ({ value, label, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  return (
    <Box  flex={1} onClick={onClick} sx={{ cursor: onClick ? 'pointer' : 'default',ml:2 }}>
      <Typography 
        variant="h6"
        sx={{ 
          fontSize: isMobile ? '1rem' : isTablet ? '1.2rem' : '1.4rem',
          // fontWeight: 600
          alignItems: 'flex-start',
        }}
      >
        {value}
      </Typography>
      <Typography 
        variant="caption"
        sx={{ 
          fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.9rem'
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// Utility component to display a list of stats with dividers
interface StatsSectionProps {
  stats: Array<{ value: number | string; label: string; onClick?: () => void }>;
}

const StatsSection: React.FC<StatsSectionProps> = ({ stats }) => (
  <Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', width: '100%'}}>
    {stats.map((stat, idx) => (
      <React.Fragment key={stat.label}>
        <StatItem value={stat.value} label={stat.label} xs={4} onClick={stat.onClick} />
        {/* {idx < stats.length - 1 && <Divider orientation="vertical" flexItem />} */}
      </React.Fragment>
    ))}
  </Grid>
);

// Thumbnail component for post media
interface PostThumbnailProps {
  post: any;
  onSelect: () => void;
}

const PostThumbnail: React.FC<PostThumbnailProps> = ({ post, onSelect }) => {
  const file = post?.files?.[0] || {};
  const isVideo = (file.fileType || '').includes('video');

  return (
    <ImageListItem
      sx={{ position: 'relative' }}
      onClick={onSelect}
      key={file.fileURL || file.fileKey}
    >
      {isVideo ? (
        <video
          src={file.fileURL}
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img
          srcSet={file.fileURL}
          src={file.fileURL}
          alt={file.fileURL}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}

      {isVideo && (
        <Box sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.4)' }}>
          <InstaVideoIcon />
        </Box>
      )}
    </ImageListItem>
  );
};


const ProfileScreen: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
  // toggle to show additional statistics on small screens
  const [showMoreStats, setShowMoreStats] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  
  // Redux state
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const authType = useSelector((state: any) => state.auth.authType);
  const user = useSelector((state: RootState) => state.user);
  const posts = useSelector((state: RootState) => state.post.posts);
  const loading = useSelector((state: RootState) => state.post.loading);
  const hasMore = useSelector((state: RootState) => state.post.hasMore);
  const currentPage = useSelector((state: RootState) => state.post.currentPage);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('login user', user);

  // Get user id from route (for visiting other users' profiles) and redux (for own profile)
  const { id: routeUserId } = useParams<{ id?: string }>();
  // Memoised statistics
  const primaryStats = useMemo(
    () => [
      { value: userData?.postsCount ?? 0, label: 'Posts' },
      { value: userData?.visitedPlacesCount ?? 0, label: 'Visited place', onClick: () => navigate('/profile/visited-places') },
      { value: userData?.tripsCount ?? 0, label: 'Trips' },
    ],
    [navigate, posts.length, userData?.postsCount, userData?.visitedPlacesCount, userData?.tripsCount],
  );

  const additionalStats = useMemo(
    () => [
      {
        value: userData?.followersCount ?? 0,
        label: 'Followers',
        onClick: () => navigate(`/profile/${userData?._id}/followers`, { state: { userId: userData?._id } }),
      },
      {
        value: userData?.followingCount ?? 0,
        label: 'Following',
        onClick: () => navigate(`/profile/${userData?._id}/following`, { state: { userId: userData?._id, type: 'following' } }),
      },
    ],
    [navigate, userData?._id, userData?.followersCount, userData?.followingCount],
  );

  /**
   * Fetch profile details for the provided user id and store/update local & redux state
   */
  const fetchProfile = async (userId: string) => {
    if (!userId) return;
    try {
      const res = await request(`/profile/${userId}`, {
        method: 'GET',
        authorization: true
      }) as any;
      // Only update global Redux user if this profile matches the logged-in user
      if (user?.user?.username && user?.user?.username === res.data.username) {
        dispatch(setUser(res.data));

      }
      setUserData(res.data);
      console.log('Profile fetched', res.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };


  const handleLogout = async () => {
    try {
      // If user is logged in via Google, sign out from Google as well
      if (authType === 'google') {
        await signOutGoogle();
      }

      // Unregister FCM token
      await unregisterFcmToken();

      // Clear any memoized user cache (usernames, profiles)
      clearUserCache();

      // Redux logout & clear token
      dispatch(clearFcmToken());
      dispatch(logout());
      dispatch(setUser(null));
      setDrawerOpen(false);
      navigate('/login');

    } catch (error) {
      console.error('Error during logout:', error);
      // Still perform local logout even if Google logout fails
      await unregisterFcmToken();
      clearUserCache();
      dispatch(clearFcmToken());
      dispatch(logout());
      dispatch(setUser(null));
      setDrawerOpen(false);
      navigate('/login');
    }
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Drawer menu configuration
  const drawerMenu = [
    { icon: <HomeIcon />, label: 'Home', action: () => navigate('/') },
    { icon: <ExploreIcon />, label: 'Trips', action: () => navigate('/trips') },
    { icon: <PaymentIcon />, label: 'Payment', action: () => navigate('/payment') },
    { icon: <SettingsIcon />, label: 'Settings', action: () => { } },
    { icon: <HelpIcon />, label: 'Help & Support', action: () => { } },
  ];

  const drawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {/* Profile header */}
        <ListItem>
          <ListItemIcon>
            <Avatar
              src={userData?.profilePhoto?.url || ''}
              sx={{ width: 40, height: 40 }}
            />
          </ListItemIcon>
          <ListItemText
            primary={userData?.username || 'User'}
            secondary={userData?.email || ''}
          />
        </ListItem>
        <Divider />

        {/* Menu items */}
        {drawerMenu.map((item) => (
          <ListItemButton key={item.label} onClick={item.action}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}

        <Divider />

        {/* Logout */}
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );

  // Extract potential user data passed via navigation state
  const locationState = location.state as { userId?: string; profile?: any } | undefined;

  // Resolve which user ID to load in this priority: route param -> location.state -> redux
  // const resolvedUserId = routeUserId || locationState?.userId || reduxUser?._id;
  const resolvedUserId = routeUserId === undefined ? user?.user?.id || user?.user?._id : routeUserId ;
  const currentUserId = user?.user?.id || user?.user?._id;
  const isCurrentUser = !routeUserId || routeUserId === currentUserId;
  const postTypeFilter = isCurrentUser ? undefined : 'public';
  console.log('resolvedUserId', user);

  // Load more posts when reaching the bottom
  const loadMorePosts = useCallback(() => {
    console.log('loadMorePosts called', { hasMore, loading, resolvedUserId, currentPage });
    if (hasMore && !loading && resolvedUserId) {
      console.log('Fetching next page:', currentPage + 1);
      fetchUserPosts(
        dispatch,
        resolvedUserId,
        currentPage + 1,
        12,
        true,
        postTypeFilter ? { type: postTypeFilter } : undefined
      );
    }
  }, [hasMore, loading, currentPage, resolvedUserId, dispatch, postTypeFilter]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        console.log('Intersection observer triggered', { 
          isIntersecting: entries[0].isIntersecting, 
          hasMore, 
          loading,
          postsLength: posts.length 
        });
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    console.log('Observing loadMoreRef element');
    observer.observe(loadMoreRef.current);
    
    return () => {
      console.log('Disconnecting observer');
      observer.disconnect();
    };
  }, [loadMorePosts, hasMore, loading]);

  // If location.state already provides full profile payload, use it immediately
  useEffect(() => {
    if (locationState?.profile) {
      setUserData(locationState.profile);
    }
  // we only want to run once on mount when locationState is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !resolvedUserId) return;

    // If we already have userData that matches the resolved id, avoid refetching profile
    if (!userData || userData?._id !== resolvedUserId) {
      fetchProfile(resolvedUserId);
    }

    // Reset posts and fetch first page
    dispatch(resetPosts());
    fetchUserPosts(
      dispatch,
      resolvedUserId,
      1,
      12,
      false,
      postTypeFilter ? { type: postTypeFilter } : undefined
    );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedUserId, postTypeFilter]);


  return (
    <Box sx={{ bgcolor: '#fff', pb: 8, px: 2, borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header with Navigation */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TopNavBar 
          title='Profile' 
          canGoBack
          rightIcon={!routeUserId ? <MenuIcon /> : undefined}
          onRightIconClick={!routeUserId ? () => navigate('/hamburger-menu') : undefined}
        />
      </Box>

      {/* Side Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawerList()}
      </Drawer>

      {/* Profile Card */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          // backgroundColor: 'blue',
        }}
      >
        <Avatar
        onClick={() => navigate('/profile/edit')}
          src={userData?.profilePhoto?.url}
          sx={{ 
            width: isMobile ? 80 : isTablet ? 100 : 120, 
            height: isMobile ? 80 : isTablet ? 100 : 120, 
            borderRadius: '50%', 
            mr: isMobile ? '2px' : '3px', 
            cursor: 'pointer' ,
          }}
        />
        <Stack direction="column" spacing={0} sx={{ 
          display: 'flex', width: '100%',
           }}>

          <Box>
            <Typography 
              variant={isMobile ? "h5" : isTablet ? "h5" : "h4"}
              sx={{ 
                fontSize: isMobile ? '1rem' : isTablet ? '1.3rem' : '1.5rem',
                // fontWeight: 600
              }}
            >
              {userData?.username}
            </Typography>
          </Box>
          {/* Stats Grid */}
          <Grid container spacing={2} >
            <StatsSection stats={primaryStats} />
          </Grid>

          {/* Additional stats when showMoreStats is true */}
          {showMoreStats && <StatsSection stats={additionalStats} />}

          {/* Toggle button */}
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Typography
              variant="body2"
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '1rem'
              }}
              onClick={() => setShowMoreStats((prev) => !prev)}
            >
              {showMoreStats ? 'Show Less' : 'More'}
            </Typography>
          </Box>
         
        </Stack>
        
      </Box>
      {userData?.bio && (
            <Box sx={{ width: '100%' }}>
              <Typography 
                onClick={() => setIsBioExpanded(!isBioExpanded)}
                sx={{ 
                  fontSize: isMobile ? '0.8rem' : isTablet ? '0.9rem' : '1rem',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  display: '-webkit-box',
                  WebkitLineClamp: isBioExpanded ? 'unset' : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }}
              >
                Bio: {userData.bio}
              </Typography>
              {userData.bio && userData.bio.length > 100 && (
                <Typography
                  variant="body2"
                  onClick={() => setIsBioExpanded(!isBioExpanded)}
                  sx={{
                    cursor: 'pointer',
                    color: 'primary.main',
                    mt: 0.5,
                    fontSize: isMobile ? '0.75rem' : isTablet ? '0.85rem' : '0.9rem',
                    fontWeight: 500,
                  }}
                >
                  {isBioExpanded ? 'Show less' : 'Show more'}
                </Typography>
              )}
            </Box>
          )}

      {
        !isCurrentUser &&
        <Box sx={{ display: 'flex', flexDirection: 'row', overflow: 'hidden', width: '100%', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2 }}>
          <Chip
            label={userData?.isFollowing ? 'Following' :userData?.isRequested ? "Requested"  : 'Follow'}
            onClick={async () => {
              try {
                const res = await followUser(userData?._id);
                // Update local userData isFollowing & counts quickly
                setUserData((prev: any) => ({
                  ...prev,
                  isFollowing: res?.isFollowing,
                  isRequested: res?.isRequested,
                  followersCount: res?.followersCount ?? prev.followersCount,
                }));
              } catch (error) {
                console.error('Follow error', error);
              }
            }}
            sx={{ mb: 1, width: '50%', backgroundColor: '#F57C00', color: '#fff' }}
          />
          <Chip
            label="Message"
            variant="outlined"
            onClick={async () => {
              if (userData && user?.user && (user?.user?.id || user?.user?._id) && userData?._id) {
                try {
                  const chatId = await getOrCreateChat(user.user._id || user.user.id, userData._id);
                  navigate(`/chat/${chatId}`, {
                    state: {
                      chatId,
                      otherUserName: userData._id || userData.id,
                      source: 'profile',
                    }
                  });
                } catch (error) {
                  console.error('Error creating chat:', error);
                  // You can add user notification here if needed
                }
              } else {
                console.warn('Cannot create chat: missing user IDs', { 
                  currentUserId: user?.user?._id || user?.user?.id, 
                  otherUserId: userData?._id 
                });
              }
            }}
            sx={{ mb: 1, width: '50%', borderColor: '#F57C00', color: '#49454F' }}
          />
        </Box>
      }


      {/* <ImageList sx={{
        width: '100%', justifyContent: 'center', alignItems: 'center', overflowY: 'hidden',
        overflowX: 'hidden',
      }} cols={3} rowHeight={164}>
        {posts.map((item) => (
          <ImageListItem key={item.files[0].fileURL}>
            <img
              srcSet={`${item.files[0].fileURL}`}
              src={`${item.files[0].fileURL}`}
              alt={item.files[0].fileURL}
              loading="lazy"
              style={{ borderRadius: '16px' }}
            />
          </ImageListItem>
        ))}
      </ImageList> */}
      <Container maxWidth="sm" sx={{ flex: 1, }}>

        <ImageList
          sx={{
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            overflowY: 'hidden',
            overflowX: 'hidden',
          }}
          cols={3}
          rowHeight={164} // unified cell height
        >
          {posts.map((item, index) => (
            <PostThumbnail
              key={item?.id || item?._id || index}
              post={item}
              onSelect={() => {
                navigate('/profile/post-detail', {
                  state: {
                    posts: posts,
                    selectedIndex: index,
                    userData: userData
                  }
                });
              }}
            />
          ))}
        </ImageList>

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Intersection observer trigger - only show when there are more posts to load */}
        {hasMore && !loading && posts.length > 0 && (
          <Box 
            ref={loadMoreRef} 
            sx={{ 
              height: '20px', 
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Scroll for more posts...
            </Typography>
          </Box>
        )}

        {/* Show message when no more posts */}
        {!hasMore && posts.length > 0 && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No more posts to load
            </Typography>
          </Box>
        )}

        {/* Show message when no posts at all */}
        {!loading && posts.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No posts found
            </Typography>
          </Box>
        )}

      </Container>

    </Box>
  );
};

export default ProfileScreen;
